import {
  Archive,
  Inbox,
  StickyNote,
  Send,
  Trash2,
  ArchiveX,
  Info,
  Users,
  MessageSquare,
  ShoppingCart,
  Plus,
} from "lucide-react";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clientRepo, useAuth, useUser } from "@zickko/client";
import { emailPortalRoutes } from "../routes/emailPortalRoutes";
import { useOrg } from "../providers/OrgProvider";

type RenderSidebarLinkProps = {
  mailboxName: String;
  icon: ReactNode;
  count: Number;
  isSelected: boolean; // which tab from sidebar is currently selected
  onClick: () => void; // Function to handle click events
};

// Function to render individual sidebar links
const renderSidebarLinks = (props: RenderSidebarLinkProps) => {
  // Styles for the selected mailbox link
  const linkStyle = props.isSelected ? { backgroundColor: "black", color: "white" } : {};

  return (
    <div className="sidebar-links" onClick={props.onClick} style={{ ...linkStyle }}>
      <div className="link-icon">
        {props.icon}
        <div className="text" style={{ color: props.isSelected ? "white" : "black" }}>
          {props.mailboxName}
        </div>
      </div>
      {props.count !== 0 && (
        <div className="count" style={{ color: props.isSelected ? "white" : "black" }}>
          {JSON.stringify(props.count)}
        </div>
      )}
    </div>
  );
};

export const EmailSidebar = () => {
  const auth = useAuth();
  const navigateTo = useNavigate();
  const location = useLocation();

  // Get the current path and convert it to a mailbox name
  const initialSelectedMailbox = () => {
    const path = location.pathname.slice(1); // Remove leading slash
    if (path === "") return "Inbox";
    return path.charAt(0).toUpperCase() + path.slice(1); // Capitalize first letter
  };

  // State variables to hold email counts and selected mailbox
  const [inboxCount, setInboxCount] = useState<number | null>(null);
  const [archiveCount, setArchiveCount] = useState<number | null>(null);
  const [draftsCount, setDraftsCount] = useState<number | null>(null);
  const [selectedMailbox, setSelectedMailbox] = useState<string>(initialSelectedMailbox());

  const { org } = useOrg();
  const { user } = useUser();

  // Update selected mailbox when URL changes
  useEffect(() => {
    setSelectedMailbox(initialSelectedMailbox());
  }, [location.pathname]);

  // Fetch the count of emails in the inbox
  const fetchInboxCount = useCallback(async () => {
    try {
      const count = await clientRepo.inbox.getCount({
        key: { orgId: org.id, uid: user.uid },
      });
      setInboxCount(count ?? 0); // Set inbox count or default to 0
    } catch (error) {
      console.error("Failed to fetch inbox count:", error);
      setInboxCount(0); // Default to 0 in case of an error
    }
  }, [org, user]);

  // Fetch the count of emails in the archive
  const fetchArchiveCount = useCallback(async () => {
    try {
      const count = await clientRepo.archive.getCount({
        key: { orgId: org.id, uid: user.uid },
      });
      setArchiveCount(count ?? 0); // Set archive count or default to 0
    } catch (error) {
      console.error("Failed to fetch archive count:", error);
      setArchiveCount(0); // Default to 0 in case of an error
    }
  }, [org, user]);

  const fetchDraftsCount = useCallback(async () => {
    try {
      const count = await clientRepo.drafts.getCount({
        key: { orgId: org.id, uid: user.uid },
      });
      setDraftsCount(count ?? 0); // Set drafts count or default to 0
    } catch (error) {
      console.error("Failed to fetch drafts count:", error);
      setDraftsCount(0); // Default to 0 in case of an error
    }
  }, [org, user]);

  // Use effect to fetch email counts on component mount and whenever org/user changes
  useEffect(() => {
    fetchInboxCount();
    fetchArchiveCount();
    fetchDraftsCount();
  }, [fetchInboxCount, fetchArchiveCount, fetchDraftsCount, org, user]);

  // Logout function
  const logout = () => {
    auth.logout();
  };

  return (
    <div className="sidebar">
      <div className="zickko-logo-container">
        <img src="/images/logo-small-zickko.svg" className="zickko-logo" onClick={() => navigateTo("/")} />
      </div>
      <div className="mailboxes">
        {renderSidebarLinks({
          mailboxName: "Compose",
          icon: <Plus size={20} />,
          count: 0,
          isSelected: selectedMailbox === "Compose",
          onClick: () => {
            setSelectedMailbox("Compose");
            navigateTo(emailPortalRoutes.compose.link({}, {}));
          },
        })}
        {renderSidebarLinks({
          mailboxName: "Inbox",
          icon: <Inbox size={20} />,
          count: inboxCount ?? 0,
          isSelected: selectedMailbox === "Inbox",
          onClick: () => {
            setSelectedMailbox("Inbox");
            navigateTo(emailPortalRoutes.home.link({}, {}));
          },
        })}
        {renderSidebarLinks({
          mailboxName: "Drafts",
          icon: <StickyNote size={20} />,
          count: draftsCount ?? 0,
          isSelected: selectedMailbox === "Drafts",
          onClick: () => {
            setSelectedMailbox("Drafts");
            navigateTo(emailPortalRoutes.drafts.link({}, {}));
          },
        })}
        {renderSidebarLinks({
          mailboxName: "Sent",
          icon: <Send size={20} />,
          count: 0,
          isSelected: selectedMailbox === "Sent",
          onClick: () => {
            setSelectedMailbox("Sent");
            navigateTo(emailPortalRoutes.outbox.link({}, {}));
          },
        })}
        {renderSidebarLinks({
          mailboxName: "Junk",
          icon: <ArchiveX size={20} />,
          count: 23,
          isSelected: selectedMailbox === "Junk",
          onClick: () => setSelectedMailbox("Junk"),
        })}
        {renderSidebarLinks({
          mailboxName: "Trash",
          icon: <Trash2 size={20} />,
          count: 0,
          isSelected: selectedMailbox === "Trash",
          onClick: () => {
            setSelectedMailbox("Trash");
            navigateTo(emailPortalRoutes.trash.link({}, {}));
          },
        })}
        {renderSidebarLinks({
          mailboxName: "Archive",
          icon: <Archive size={20} />,
          count: archiveCount ?? 0,
          isSelected: selectedMailbox === "Archive",
          onClick: () => {
            setSelectedMailbox("Archive");
            navigateTo(emailPortalRoutes.archive.link({}, {}));
          },
        })}
      </div>
      <div className="categories">
        {renderSidebarLinks({
          mailboxName: "Social",
          icon: <Users size={20} />,
          count: 972,
          isSelected: selectedMailbox === "Social",
          onClick: () => setSelectedMailbox("Social"),
        })}
        {renderSidebarLinks({
          mailboxName: "Updates",
          icon: <Info size={20} />,
          count: 342,
          isSelected: selectedMailbox === "Updates",
          onClick: () => setSelectedMailbox("Updates"),
        })}
        {renderSidebarLinks({
          mailboxName: "Forums",
          icon: <MessageSquare size={20} />,
          count: 128,
          isSelected: selectedMailbox === "Forums",
          onClick: () => setSelectedMailbox("Forums"),
        })}
        {renderSidebarLinks({
          mailboxName: "Shopping",
          icon: <ShoppingCart size={20} />,
          count: 8,
          isSelected: selectedMailbox === "Shopping",
          onClick: () => setSelectedMailbox("Shopping"),
        })}
        {renderSidebarLinks({
          mailboxName: "Promotions",
          icon: <Archive size={20} />,
          count: 21,
          isSelected: selectedMailbox === "Promotions",
          onClick: () => setSelectedMailbox("Promotions"),
        })}
      </div>
      <div className="logout-button-container">
        <div onClick={logout} className="logout-button" style={{ backgroundColor: "black", color: "white" }}>
          <div className="logout-icon">
            <div className="text">Log Out</div>
            <i className="las la-sign-out-alt"></i>
          </div>
        </div>
      </div>
    </div>
  );
};
